import { type ReactNode } from "react";
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";

import { cn } from "@/lib/utils";
import type { Ticket, TicketStatus } from "@/services/ticketsService";
import { useSetTicketStatus } from "@/hooks/useTickets";
import { STATUSES } from "./ticketConstants";
import { TicketCard } from "./TicketCard";

function DraggableCard({ id, disabled, children }: { id: string; disabled: boolean; children: ReactNode }) {
  const { setNodeRef, listeners, transform, isDragging } = useDraggable({ id, disabled });
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 30 }
    : undefined;
  return (
    <div ref={setNodeRef} style={style} {...(disabled ? {} : listeners)} className={cn("touch-none", isDragging && "opacity-60")}>
      {children}
    </div>
  );
}

function Column({ status, children }: { status: TicketStatus; children: ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  return (
    <div
      ref={setNodeRef}
      className={cn("rounded-xl border border-border bg-muted/40 p-3 transition-colors", isOver && "ring-2 ring-ring bg-coral-light/40")}
    >
      {children}
    </div>
  );
}

export function KanbanView({
  tickets,
  isStaff,
  isCrossTenant,
  onOpen,
}: {
  tickets: Ticket[];
  isStaff: boolean;
  isCrossTenant: boolean;
  onOpen: (id: string) => void;
}) {
  const setStatus = useSetTicketStatus();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (e: DragEndEvent) => {
    if (!e.over) return;
    const id = String(e.active.id);
    const target = String(e.over.id) as TicketStatus;
    const ticket = tickets.find((t) => t.id === id);
    if (ticket && ticket.status !== target && STATUSES.some((s) => s.key === target)) {
      setStatus.mutate({ id, status: target });
    }
  };

  const board = (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {STATUSES.map((st) => {
        const inCol = tickets.filter((t) => t.status === st.key);
        return (
          <Column key={st.key} status={st.key}>
            <div className={cn("mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold", st.style)}>
              <span className={cn("h-2 w-2 rounded-full", st.dot)} />
              {st.label}
              <span className="rounded-full bg-card/70 px-1.5 tabular-nums">{inCol.length}</span>
            </div>
            <div className="min-h-[48px] space-y-2.5">
              {inCol.length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">No tickets</p>
              ) : (
                inCol.map((t) =>
                  isStaff ? (
                    <DraggableCard key={t.id} id={t.id} disabled={false}>
                      <TicketCard ticket={t} showTenant={isCrossTenant} onOpen={() => onOpen(t.id)} />
                    </DraggableCard>
                  ) : (
                    <TicketCard key={t.id} ticket={t} showTenant={isCrossTenant} onOpen={() => onOpen(t.id)} />
                  ),
                )
              )}
            </div>
          </Column>
        );
      })}
    </div>
  );

  return isStaff ? (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      {board}
    </DndContext>
  ) : (
    board
  );
}
